import { useEffect, useRef, useState, Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Bounds, Environment, Lightformer, Html, TransformControls } from "@react-three/drei";
import { Part3D } from "@/pages/Part3D";
import api, { apiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, UploadSimple, Trash, CircleNotch, FolderSimple, Palette, PencilSimple, Cube, MagicWand, Image as ImageIcon, ArrowClockwise, ArrowsCounterClockwise, ArrowsOutCardinal } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import CategoriesManager from "@/components/CategoriesManager";

const emptyCol = { id: null, name: "", theme_color: "#D4AF37", useUniversity: true, useCustom: false, stones: [], enamels: [], official_logo_id: "", official_enamel: "", group_label: "" };

function Logo3DPreview({ modelUrl, orient, prepared, height = 150 }) {
  // Prepared (designer) logos keep their baked curve/thickness/orientation — never normalize
  // or auto-orient them (that's what distorted them); just centre for a tidy preview.
  const part = prepared
    ? { model_url: modelUrl, role: "logo", normalize: false, autoOrient: false, centered: true, color: "#C0C0C0" }
    : { model_url: modelUrl, role: "logo", normalize: true, orient, color: "#C0C0C0" };
  return (
    <div style={{ height }} className="rounded-lg overflow-hidden bg-neutral-200">
      <Canvas camera={{ position: [0, 0, 2.4], fov: 45 }} gl={{ preserveDrawingBuffer: true }}>
        <color attach="background" args={["#e9e7e3"]} />
        <ambientLight intensity={0.6} />
        <directionalLight position={[4, 6, 5]} intensity={1.6} />
        <Environment resolution={128} frames={1}>
          <Lightformer form="rect" intensity={3} position={[0, 4, -5]} scale={[8, 5, 1]} />
        </Environment>
        <Suspense fallback={<Html center><CircleNotch className="animate-spin" size={20} /></Html>}>
          <Bounds fit margin={1.2}>
            <Part3D part={part} colorOverride="#C0C0C0" />
          </Bounds>
        </Suspense>
        <OrbitControls makeDefault enablePan={false} autoRotate autoRotateSpeed={2.5} />
      </Canvas>
    </div>
  );
}

// Mini gizmo to freely rotate a 3D logo. The wrapping group's rotation IS the saved
// `orient` (Part3D renders with orient=0 here so there's no double rotation).
function LogoGizmo({ logo, open, onOpenChange, onSaved }) {
  const { t } = useTranslation();
  const ref = useRef();
  const [euler, setEuler] = useState(logo.orient || [0, 0, 0]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setEuler(logo.orient || [0, 0, 0]); }, [open, logo.orient]);
  const commit = () => { const o = ref.current; if (o) setEuler([o.rotation.x, o.rotation.y, o.rotation.z]); };
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/logos/${logo.id}`, { orient: euler }); onSaved(euler); toast.success(t("logos.t_orientSaved")); onOpenChange(false); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-white/10 text-neutral-200 max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-xl">{t("logos.gizmoTitle", { name: logo.name })}</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-400 -mt-2">{t("logos.gizmoHint")}</p>
        <div className="h-[320px] rounded-lg overflow-hidden bg-neutral-200" data-testid={`logo-gizmo-canvas-${logo.id}`}>
          <Canvas camera={{ position: [0, 0, 2.6], fov: 45 }} gl={{ preserveDrawingBuffer: true }}>
            <color attach="background" args={["#e9e7e3"]} />
            <ambientLight intensity={0.6} />
            <directionalLight position={[4, 6, 5]} intensity={1.6} />
            <Environment resolution={128} frames={1}><Lightformer form="rect" intensity={3} position={[0, 4, -5]} scale={[8, 5, 1]} /></Environment>
            <Suspense fallback={<Html center><CircleNotch className="animate-spin" size={20} /></Html>}>
              <group ref={ref} rotation={euler}>
                <Part3D part={{ model_url: logo.model_url, role: "logo", normalize: true, autoOrient: false, color: "#C0C0C0" }} colorOverride="#C0C0C0" />
              </group>
              <TransformControls object={ref} mode="rotate" size={0.9} onMouseUp={commit} />
            </Suspense>
            <OrbitControls makeDefault enablePan={false} />
          </Canvas>
        </div>
        <div className="flex justify-between items-center gap-2">
          <Button variant="outline" onClick={() => setEuler([0, 0, 0])} className="rounded-full border-white/15 h-9 text-xs" data-testid={`logo-gizmo-reset-${logo.id}`}>{t("logos.reset")}</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid={`logo-gizmo-save-${logo.id}`}>{saving ? <CircleNotch className="animate-spin" /> : t("logos.saveOrient")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function Logos() {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [logos, setLogos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [colOpen, setColOpen] = useState(false);
  const [colForm, setColForm] = useState(emptyCol);
  const [uploading, setUploading] = useState(false);
  const [pendingCol, setPendingCol] = useState("none");
  const [palettes, setPalettes] = useState({ stones: [], enamels: [] });
  const [gizmoLogo, setGizmoLogo] = useState(null);
  const [savingPal, setSavingPal] = useState(false);
  const fileRef = useRef();

  // ---- 3D logo generator ----
  const [genOpen, setGenOpen] = useState(false);
  const [genMode, setGenMode] = useState("image"); // image | text
  const [genName, setGenName] = useState("");
  const [genDesc, setGenDesc] = useState("");
  const [genInvert, setGenInvert] = useState(false);
  const [genRelief, setGenRelief] = useState(0.3);
  const [genCol, setGenCol] = useState("none");
  const [genFile, setGenFile] = useState(null);
  const [genPreviewImg, setGenPreviewImg] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [genMeshyMsg, setGenMeshyMsg] = useState("");
  const genFileRef = useRef();
  const model3dRef = useRef();
  const [uploadingModel, setUploadingModel] = useState(false);

  const CHUNK = 4 * 1024 * 1024;
  const uploadModel3d = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["obj", "glb", "gltf", "stl", "ply"].includes(ext)) { toast.error(t("logos.t_unsupported")); return; }
    setUploadingModel(true);
    const uploadId = Math.random().toString(36).slice(2) + Date.now();
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId); fd.append("index", i); fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post("/logos3d/model/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const name = file.name.replace(/\.[^.]+$/, "").replace(/Meshy_AI_|_image-to-3d-texture|_\d+/g, "").replace(/_/g, " ").trim() || "3D model";
      await api.post("/logos3d/model/finalize", { upload_id: uploadId, filename: file.name, name, collection_id: pendingCol !== "none" ? pendingCol : null });
      toast.success(t("logos.t_model3dUploaded")); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingModel(false); if (model3dRef.current) model3dRef.current.value = ""; }
  };

  const pollMeshy = async (logoId) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      const { data } = await api.get(`/logos3d/meshy/status/${logoId}`);
      if (data.status === "done") return data;
      setGenMeshyMsg(t("logos.meshyProgress", { p: data.progress || 0 }));
    }
    throw new Error("Meshy timed out. Check the library in a few minutes.");
  };

  const pickGenFile = (f) => {
    if (!f) return;
    setGenFile(f);
    setGenPreviewImg(URL.createObjectURL(f));
    if (!genName) setGenName(f.name.replace(/\.[^.]+$/, ""));
    setGenResult(null);
  };

  const [converting, setConverting] = useState(null);
  const convertTo3D = async (logo) => {
    setConverting(logo.id);
    try {
      await api.post("/logos3d/generate", { source: "image", image_asset_id: logo.asset_id, name: logo.name, logo_id: logo.id });
      toast.success(t("logos.t_convertedTo3D"));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setConverting(null); }
  };
  const assignCollection = async (logo, cid) => {
    try {
      await api.patch(`/logos/${logo.id}`, { collection_id: cid === "none" ? null : cid });
      toast.success(t("logos.t_collectionUpdated"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const renameLogo = async (logo, name) => {
    try {
      await api.patch(`/logos/${logo.id}`, { name });
      toast.success(t("logos.t_nameUpdated"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const assignClass = async (logo, klass) => {
    try {
      await api.patch(`/logos/${logo.id}`, { klass });
      toast.success(t("logos.t_classUpdated"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const HALF_PI = Math.PI / 2;
  const rotateLogo = async (logo, axis) => {
    const cur = Array.isArray(logo.orient) ? [...logo.orient] : [0, 0, 0];
    cur[axis] = Math.round(((cur[axis] || 0) / HALF_PI + 1)) % 4 * HALF_PI; // +90° step, wrap
    // optimistic UI so the preview turns immediately
    setLogos((prev) => prev.map((x) => (x.id === logo.id ? { ...x, orient: cur } : x)));
    try { await api.patch(`/logos/${logo.id}`, { orient: cur }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const generate3D = async () => {
    if (genMode === "meshy") {
      if (!genFile) { toast.error(t("logos.t_uploadImage")); return; }
      setGenBusy(true); setGenResult(null); setGenMeshyMsg(t("logos.generatingMeshy"));
      try {
        const fd = new FormData(); fd.append("file", genFile); fd.append("kind", "logo_src");
        const { data: up } = await api.post("/assets/upload", fd);
        const { data: started } = await api.post("/logos3d/meshy/start", {
          image_asset_id: up.asset_id, name: genName || "Logo Meshy", collection_id: genCol !== "none" ? genCol : null });
        const done = await pollMeshy(started.logo_id);
        setGenResult({ model_url: done.model_url, obj_url: done.obj_url });
        toast.success(t("logos.t_meshyGenerated"));
        load();
      } catch (e) { toast.error(apiError(e)); }
      finally { setGenBusy(false); setGenMeshyMsg(""); }
      return;
    }
    setGenBusy(true); setGenResult(null);
    try {
      const payload = { name: genName || "Logo 3D", invert: genInvert, relief_height: genRelief,
        collection_id: genCol !== "none" ? genCol : null, save_to_library: true };
      if (genMode === "image") {
        if (!genFile) { toast.error(t("logos.t_uploadImage")); setGenBusy(false); return; }
        const fd = new FormData(); fd.append("file", genFile); fd.append("kind", "logo_src");
        const { data: up } = await api.post("/assets/upload", fd);
        payload.source = "image"; payload.image_asset_id = up.asset_id;
      } else {
        if (!genDesc.trim()) { toast.error(t("logos.t_describeLogo")); setGenBusy(false); return; }
        payload.source = "text"; payload.description = genDesc.trim();
      }
      const { data } = await api.post("/logos3d/generate", payload);
      setGenResult(data);
      toast.success(t("logos.t_generated"));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenBusy(false); }
  };

  const load = () => {
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
    api.get("/logos").then((r) => setLogos(r.data)).catch(() => {});
    api.get("/palettes").then((r) => setPalettes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setColor = (grp, i, key, val) => setPalettes((p) => ({ ...p, [grp]: p[grp].map((c, j) => j === i ? { ...c, [key]: val } : c) }));
  const addColor = (grp) => setPalettes((p) => ({ ...p, [grp]: [...p[grp], { name: t("logos.newColor"), hex: "#cccccc" }] }));
  const delColor = (grp, i) => setPalettes((p) => ({ ...p, [grp]: p[grp].filter((_, j) => j !== i) }));
  const savePalettes = async () => {
    setSavingPal(true);
    try { const { data } = await api.put("/palettes", palettes); setPalettes(data); toast.success(t("logos.t_colorsSaved")); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingPal(false); }
  };

  // per-collection color editor
  const setCF = (grp, i, key, val) => setColForm((f) => ({ ...f, [grp]: f[grp].map((c, j) => j === i ? { ...c, [key]: val } : c) }));
  const addCF = (grp) => setColForm((f) => ({ ...f, [grp]: [...(f[grp] || []), { name: t("logos.newColor"), hex: "#cccccc" }] }));
  const delCF = (grp, i) => setColForm((f) => ({ ...f, [grp]: f[grp].filter((_, j) => j !== i) }));

  const openNew = () => { setColForm({ ...emptyCol, stones: palettes.stones, enamels: palettes.enamels }); setColOpen(true); };
  const openEdit = (c) => setColForm({ id: c.id, name: c.name, theme_color: c.theme_color || "#D4AF37",
    useUniversity: (c.role || (c.type === "university" ? "primary" : "general")) === "primary",
    group_label: c.group_label || "",
    official_logo_id: c.official_logo_id || "", official_enamel: c.official_enamel || "",
    useCustom: !!(c.stones || c.enamels), stones: c.stones || palettes.stones, enamels: c.enamels || palettes.enamels }) || setColOpen(true);

  const saveCol = async () => {
    if (!colForm.name) return;
    const payload = { name: colForm.name, type: colForm.useUniversity ? "university" : "global",
      role: colForm.useUniversity ? "primary" : "general", group_label: colForm.group_label || null,
      theme_color: colForm.theme_color,
      official_logo_id: colForm.official_logo_id || null, official_enamel: colForm.official_enamel || null,
      stones: colForm.useCustom ? colForm.stones : null, enamels: colForm.useCustom ? colForm.enamels : null };
    try {
      if (colForm.id) await api.put(`/collections/${colForm.id}`, payload);
      else await api.post("/collections", payload);
      toast.success(t("logos.t_collectionSaved")); setColOpen(false); setColForm(emptyCol); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delCol = async (id) => { await api.delete(`/collections/${id}`); toast.success(t("logos.t_deleted")); load(); };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("kind", "logo");
        const { data } = await api.post("/assets/upload", fd);
        const lf = new FormData();
        lf.append("name", f.name.replace(/\.[^.]+$/, "")); lf.append("asset_id", data.asset_id);
        if (pendingCol !== "none") lf.append("collection_id", pendingCol);
        await api.post("/logos", lf);
      }
      toast.success(t("logos.t_uploaded")); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const delLogo = async (id) => { await api.delete(`/logos/${id}`); toast.success(t("logos.t_deleted")); load(); };

  const shown = filter === "all" ? logos : logos.filter((l) => l.collection_id === filter);
  const colName_ = (id) => collections.find((c) => c.id === id)?.name;

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("logos.title")}</h1>
          <p className="text-neutral-400 mt-1">{t("logos.subtitle")}</p>
        </div>
        <Dialog open={colOpen} onOpenChange={setColOpen}>
          <DialogTrigger asChild><Button onClick={openNew} variant="outline" className="rounded-full border-white/15" data-testid="new-collection-btn"><FolderSimple className="mr-1" /> {t("logos.newCollection")}</Button></DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-neutral-200 max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{colForm.id ? t("logos.editCollection") : t("logos.newCollection")}</DialogTitle></DialogHeader>
            <Label>{t("logos.colName")}</Label>
            <Input value={colForm.name} onChange={(e) => setColForm({ ...colForm, name: e.target.value })} className="bg-white/5 border-white/15" data-testid="collection-name-input" />
            <div className="flex items-center gap-3 mt-2">
              <Label className="mb-0">{t("logos.colorTheme")}</Label>
              <input type="color" value={colForm.theme_color} onChange={(e) => setColForm({ ...colForm, theme_color: e.target.value })} className="w-10 h-9 rounded border border-white/15 bg-transparent cursor-pointer" data-testid="collection-theme-color" />
              <span className="text-xs text-neutral-500 font-mono">{colForm.theme_color}</span>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer bg-white/5 rounded-lg px-3 py-2">
              <input type="checkbox" checked={colForm.useUniversity} onChange={(e) => setColForm({ ...colForm, useUniversity: e.target.checked })} data-testid="collection-is-university" />
              <span><b>{t("logos.primaryCollection")}</b> <span className="text-neutral-500">{t("logos.primaryNote")}</span></span>
            </label>
            <div className="mt-2">
              <Label className="text-xs text-neutral-300">{t("logos.groupLabel")}</Label>
              <Input value={colForm.group_label} onChange={(e) => setColForm({ ...colForm, group_label: e.target.value })} placeholder={t("logos.groupLabelPlaceholder")} className="mt-1 bg-white/5 border-white/15 h-9" data-testid="collection-group-label" />
            </div>
            {colForm.useUniversity && (
              <div className="grid grid-cols-2 gap-3 mt-2 bg-white/5 rounded-lg p-3">
                <div>
                  <Label className="text-xs text-neutral-300">{t("logos.officialLogo")}</Label>
                  <select value={colForm.official_logo_id} onChange={(e) => setColForm({ ...colForm, official_logo_id: e.target.value })}
                    data-testid="collection-official-logo" className="w-full mt-1 bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                    <option value="">{t("logos.none")}</option>
                    {logos.filter((l) => l.is_3d).map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
                  </select>
                  <p className="text-[11px] text-neutral-500 mt-1">{t("logos.officialLogoNote")}</p>
                </div>
                <div>
                  <Label className="text-xs text-neutral-300">{t("logos.officialEnamel")}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={colForm.official_enamel || "#9B1B1B"} onChange={(e) => setColForm({ ...colForm, official_enamel: e.target.value })}
                      data-testid="collection-official-enamel" className="w-10 h-9 rounded border border-white/15 bg-transparent cursor-pointer" />
                    <span className="text-xs text-neutral-500 font-mono flex-1">{colForm.official_enamel || "—"}</span>
                    {colForm.official_enamel && <button onClick={() => setColForm({ ...colForm, official_enamel: "" })} className="text-neutral-500 hover:text-red-400"><Trash size={13} /></button>}
                  </div>
                  <p className="text-[11px] text-neutral-500 mt-1">{t("logos.officialEnamelNote")}</p>
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
              <input type="checkbox" checked={colForm.useCustom} onChange={(e) => setColForm({ ...colForm, useCustom: e.target.checked })} data-testid="collection-custom-palette" />
              {t("logos.customPalette")}
            </label>
            {colForm.useCustom && (
              <div className="grid grid-cols-2 gap-4 mt-1">
                {[["stones", t("logos.stones")], ["enamels", t("logos.enamels")]].map(([grp, label]) => (
                  <div key={grp}>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-neutral-300">{label}</Label>
                      <button onClick={() => addCF(grp)} className="text-xs text-gold flex items-center gap-1"><Plus size={11} /> {t("logos.add")}</button>
                    </div>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {(colForm[grp] || []).map((c, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <input type="color" value={c.hex} onChange={(e) => setCF(grp, i, "hex", e.target.value)} className="w-7 h-7 rounded border border-white/15 bg-transparent cursor-pointer" />
                          <Input value={c.name} onChange={(e) => setCF(grp, i, "name", e.target.value)} className="bg-white/5 border-white/15 h-7 text-xs flex-1" />
                          <button onClick={() => delCF(grp, i)} className="text-neutral-500 hover:text-red-400"><Trash size={13} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={saveCol} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold mt-2" data-testid="create-collection-submit">{t("logos.save")}</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6"><CategoriesManager /></div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-sm ${filter === "all" ? "bg-gold text-obsidian" : "bg-white/5 text-neutral-400"}`}>{t("logos.all")}</button>
        {collections.map((c) => (
          <div key={c.id} className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-white/5 border border-white/10">
            <button onClick={() => setFilter(c.id)} className={`px-2 py-1 rounded-full text-sm flex items-center gap-1.5 ${filter === c.id ? "bg-gold text-obsidian" : "text-neutral-300"}`}>
              <span className="w-2.5 h-2.5 rounded-full border border-black/20" style={{ background: c.theme_color || "#888" }} /> {c.name}
              <span className={`text-[9px] px-1 rounded ${(c.type || "university") === "university" ? "bg-gold/20 text-gold" : "bg-white/15 text-neutral-300"}`}>{(c.type || "university") === "university" ? "PRINCIPAL" : "GENERAL"}</span>
            </button>
            <button onClick={() => openEdit(c)} className="text-neutral-300 hover:text-gold p-1 rounded-full hover:bg-white/10" title={t("logos.editCollection")} data-testid={`edit-col-${c.id}`}><PencilSimple size={13} /></button>
            <button onClick={() => delCol(c.id)} className="text-neutral-400 hover:text-red-400 p-1 rounded-full hover:bg-white/10" title={t("logos.t_deleted")}><Trash size={12} /></button>
          </div>
        ))}
      </div>

      <div className="border border-white/10 rounded-xl p-5 bg-[#121212] mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-sm text-neutral-400">{t("logos.addToCollection")}</Label>
            <Select value={pendingCol} onValueChange={setPendingCol}>
              <SelectTrigger className="w-56 mt-1 bg-white/5 border-white/15" data-testid="upload-collection-select"><SelectValue placeholder={t("logos.noCollection")} /></SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/15 text-neutral-200">
                <SelectItem value="none">{t("logos.noCollection")}</SelectItem>
                {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="upload-logo-btn">
            {uploading ? <CircleNotch className="animate-spin" /> : <><UploadSimple className="mr-1" /> {t("logos.uploadLogos")}</>}
          </Button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onUpload} />
          <Button onClick={() => model3dRef.current?.click()} disabled={uploadingModel} variant="outline" className="rounded-full border-white/15" data-testid="upload-model3d-btn">
            {uploadingModel ? <CircleNotch className="animate-spin" /> : <><Cube className="mr-1" /> {t("logos.uploadModel3d")}</>}
          </Button>
          <input ref={model3dRef} type="file" accept=".obj,.glb,.gltf,.stl,.ply" hidden onChange={(e) => { uploadModel3d(e.target.files[0]); }} />
        </div>
      </div>

      {/* 3D Logo generator */}
      <div className="border border-gold/25 rounded-xl p-5 bg-gradient-to-b from-gold/[0.06] to-transparent mb-6" data-testid="logo3d-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Cube size={18} className="text-gold" /> {t("logos.gen3dTitle")}</h3>
            <p className="text-xs text-neutral-400 mt-1">{t("logos.gen3dDesc")}</p>
          </div>
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild><Button className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="open-logo3d-btn"><MagicWand className="mr-1" /> {t("logos.createLogo3d")}</Button></DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-neutral-200 max-h-[88vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{t("logos.createLogo3d")}</DialogTitle></DialogHeader>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setGenMode("image")} className={`flex-1 min-w-[120px] py-2 rounded-lg border text-sm flex items-center justify-center gap-1.5 ${genMode === "image" ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400"}`} data-testid="gen-mode-image"><ImageIcon size={15} /> {t("logos.modeReliefLocal")}</button>
                <button onClick={() => setGenMode("meshy")} className={`flex-1 min-w-[120px] py-2 rounded-lg border text-sm flex items-center justify-center gap-1.5 ${genMode === "meshy" ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400"}`} data-testid="gen-mode-meshy"><Cube size={15} /> {t("logos.modeMeshy")}</button>
                <button onClick={() => setGenMode("text")} className={`flex-1 min-w-[120px] py-2 rounded-lg border text-sm flex items-center justify-center gap-1.5 ${genMode === "text" ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400"}`} data-testid="gen-mode-text"><MagicWand size={15} /> {t("logos.modeText")}</button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-2">
                <div className="space-y-3">
                  {genMode !== "text" ? (
                    <div>
                      <Label className="text-sm">{t("logos.logoImage")}</Label>
                      <input ref={genFileRef} type="file" accept="image/*" hidden onChange={(e) => pickGenFile(e.target.files[0])} />
                      <div onClick={() => genFileRef.current?.click()} className="mt-1 border border-dashed border-white/20 rounded-lg h-40 flex items-center justify-center cursor-pointer bg-white/5 overflow-hidden" data-testid="gen-image-drop">
                        {genPreviewImg ? <img src={genPreviewImg} alt="src" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-neutral-500 flex flex-col items-center gap-1"><UploadSimple size={22} /> {t("logos.clickUpload")}</span>}
                      </div>
                      <p className="text-[11px] text-neutral-500 mt-1">{genMode === "meshy" ? t("logos.meshyHint") : t("logos.imageHint")}</p>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-sm">{t("logos.describeLogo")}</Label>
                      <Textarea value={genDesc} onChange={(e) => setGenDesc(e.target.value)} rows={5} placeholder={t("logos.describePlaceholder")} className="bg-white/5 border-white/15 mt-1" data-testid="gen-desc-input" />
                      <p className="text-[11px] text-neutral-500 mt-1">{t("logos.googleHint")}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm">{t("logos.name")}</Label>
                    <Input value={genName} onChange={(e) => setGenName(e.target.value)} className="bg-white/5 border-white/15 mt-1" data-testid="gen-name-input" />
                  </div>
                  <div>
                    <Label className="text-sm">{t("logos.collectionOptional")}</Label>
                    <Select value={genCol} onValueChange={setGenCol}>
                      <SelectTrigger className="mt-1 bg-white/5 border-white/15" data-testid="gen-collection-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/15 text-neutral-200">
                        <SelectItem value="none">{t("logos.noCollection")}</SelectItem>
                        {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {genMode === "image" && (
                    <>
                      <div>
                        <div className="flex items-center justify-between"><Label className="text-sm">{t("logos.reliefDepth")}</Label><span className="text-xs text-neutral-500">{Math.round(genRelief * 100)}%</span></div>
                        <input type="range" min="0.1" max="0.6" step="0.02" value={genRelief} onChange={(e) => setGenRelief(parseFloat(e.target.value))} className="w-full accent-[#d4af37]" data-testid="gen-relief-slider" />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer"><input type="checkbox" checked={genInvert} onChange={(e) => setGenInvert(e.target.checked)} data-testid="gen-invert" /> {t("logos.invert")}</label>
                    </>
                  )}
                  {genMeshyMsg && <p className="text-[11px] text-gold flex items-center gap-1"><CircleNotch className="animate-spin" size={12} /> {genMeshyMsg}</p>}
                  <Button onClick={generate3D} disabled={genBusy} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="gen-submit-btn">
                    {genBusy ? <><CircleNotch className="animate-spin mr-1" /> {genMode === "meshy" ? t("logos.generatingMeshy") : t("logos.generating")}</> : <><MagicWand className="mr-1" /> {genMode === "meshy" ? t("logos.generateMeshy") : t("logos.generateRelief")}</>}
                  </Button>
                </div>
                <div>
                  <Label className="text-sm">{t("logos.preview3d")}</Label>
                  <div className="mt-1">
                    {genResult ? <Logo3DPreview modelUrl={genResult.model_url} height={280} /> : <div className="h-[280px] rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-xs text-neutral-500">{t("logos.previewPlaceholder")}</div>}
                  </div>
                  {genResult && <p className="text-[11px] text-gold mt-2 flex items-center gap-1"><Cube size={12} /> {t("logos.savedLibrary")}</p>}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Palettes */}
      <div className="border border-white/10 rounded-xl p-5 bg-[#121212] mb-6" data-testid="palettes-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><Palette size={18} className="text-gold" /> {t("logos.palettesTitle")}</h3>
          <Button onClick={savePalettes} disabled={savingPal} size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-palettes-btn">
            {savingPal ? <CircleNotch className="animate-spin" /> : t("logos.saveColors")}
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {[["stones", t("logos.stones")], ["enamels", t("logos.enamels")]].map(([grp, label]) => (
            <div key={grp}>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm text-neutral-300">{label}</Label>
                <button onClick={() => addColor(grp)} className="text-xs text-gold flex items-center gap-1" data-testid={`add-${grp}`}><Plus size={12} /> {t("logos.add")}</button>
              </div>
              <div className="space-y-2">
                {(palettes[grp] || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`${grp}-row-${i}`}>
                    <input type="color" value={c.hex} onChange={(e) => setColor(grp, i, "hex", e.target.value)} className="w-9 h-9 rounded border border-white/15 bg-transparent cursor-pointer" />
                    <Input value={c.name} onChange={(e) => setColor(grp, i, "name", e.target.value)} className="bg-white/5 border-white/15 h-9 flex-1" />
                    <button onClick={() => delColor(grp, i)} className="text-neutral-500 hover:text-red-400 p-1"><Trash size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-400">
          <Plus size={36} className="mx-auto mb-3 text-neutral-600" /> {t("logos.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="logos-grid">
          {shown.map((l) => (
            <div key={l.id} className="group relative border border-white/10 rounded-xl overflow-hidden bg-white" data-testid={`logo-card-${l.id}`}>
              {l.is_3d ? (
                <Logo3DPreview modelUrl={l.model_url} orient={l.orient} prepared={l.prepared} height={130} />
              ) : (
                <div className="aspect-square p-3 flex items-center justify-center"><img src={assetUrl(l.url)} alt={l.name} className="max-w-full max-h-full object-contain" /></div>
              )}
              {l.is_3d && <span className="absolute top-1.5 left-1.5 bg-gold text-obsidian text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Cube size={9} /> 3D</span>}
              {l.is_3d && (
                <div className="absolute bottom-[52px] left-1/2 -translate-x-1/2 flex gap-1 bg-black/55 rounded-full px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`logo-orient-${l.id}`}>
                  <button onClick={() => rotateLogo(l, 1)} title="Girar cara (eje Y) 90°" className="p-1 rounded-full hover:bg-white/15" data-testid={`logo-rot-y-${l.id}`}><ArrowClockwise size={13} className="text-white" /></button>
                  <button onClick={() => rotateLogo(l, 0)} title="Inclinar (eje X) 90°" className="p-1 rounded-full hover:bg-white/15" data-testid={`logo-rot-x-${l.id}`}><ArrowsOutCardinal size={13} className="text-white" /></button>
                  <button onClick={() => rotateLogo(l, 2)} title="Girar plano (eje Z) 90°" className="p-1 rounded-full hover:bg-white/15" data-testid={`logo-rot-z-${l.id}`}><ArrowsCounterClockwise size={13} className="text-white" /></button>
                </div>
              )}
              {!l.is_3d && l.meshy_status === "processing" && <span className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1"><CircleNotch className="animate-spin" size={9} /> Meshy…</span>}
              <button onClick={() => delLogo(l.id)} className="absolute top-1.5 right-1.5 bg-black/70 rounded-full p-1 opacity-0 group-hover:opacity-100"><Trash size={12} className="text-white" /></button>
              <div className="px-2 py-1.5 bg-[#121212] space-y-1.5">
                <Input defaultValue={l.name} key={l.id + "-" + l.name}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== l.name) renameLogo(l, v); }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  className="h-6 bg-white/5 border-white/10 text-[11px] px-1.5" data-testid={`logo-name-${l.id}`} title="Editar nombre del logo" />
                {l.is_3d && (
                  <Button onClick={() => setGizmoLogo(l)} size="sm" variant="outline" className="w-full h-7 rounded-full border-white/15 text-[11px]" data-testid={`logo-gizmo-open-${l.id}`}>
                    <ArrowsOutCardinal size={12} className="mr-1" /> {t("logos.orient")}
                  </Button>
                )}
                {l.obj_url && <a href={assetUrl(l.obj_url)} download={`${l.name}.obj`} className="text-[10px] text-gold flex items-center gap-1 hover:underline" data-testid={`logo-obj-${l.id}`}><Cube size={10} /> {t("logos.downloadObj")}</a>}
                {!l.is_3d && l.meshy_status !== "processing" && l.url && (
                  <Button onClick={() => convertTo3D(l)} disabled={converting === l.id} size="sm" className="w-full h-7 rounded-full bg-gold/90 text-obsidian hover:bg-gold text-[11px] font-semibold" data-testid={`convert-3d-${l.id}`}>
                    {converting === l.id ? <CircleNotch className="animate-spin" size={12} /> : <><Cube size={12} className="mr-1" /> {t("logos.convertTo3D")}</>}
                  </Button>
                )}
                <Select value={l.collection_id || "none"} onValueChange={(v) => assignCollection(l, v)}>
                  <SelectTrigger className="h-7 bg-white/5 border-white/15 text-[11px]" data-testid={`logo-collection-${l.id}`}><SelectValue placeholder={t("logos.collectionPlaceholder")} /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/15 text-neutral-200">
                    <SelectItem value="none">{t("logos.noCollection")}</SelectItem>
                    {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <select value={l.klass || "global"} onChange={(e) => assignClass(l, e.target.value)}
                  className="w-full h-7 bg-white/5 border border-white/15 rounded-md text-[11px] px-1.5 text-neutral-300" data-testid={`logo-class-${l.id}`} title="Clase del logo">
                  <option value="global">{t("logos.classGlobal")}</option>
                  <option value="joyeria">{t("logos.classJoyeria")}</option>
                  <option value="ilustracion">{t("logos.classIlustracion")}</option>
                  <option value="modelo3d">{t("logos.classModelo3d")}</option>
                  <option value="mueble">{t("logos.classMueble")}</option>
                  <option value="accesorio">{t("logos.classAccesorio")}</option>
                  <option value="otro">{t("logos.classOtro")}</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
      {gizmoLogo && (
        <LogoGizmo logo={gizmoLogo} open={!!gizmoLogo} onOpenChange={(v) => !v && setGizmoLogo(null)}
          onSaved={(euler) => setLogos((prev) => prev.map((x) => (x.id === gizmoLogo.id ? { ...x, orient: euler } : x)))} />
      )}
    </div>
  );
}
